import { useReactiveVar } from '@apollo/client';
import { useLocation, useNavigate } from '@hulu/react-router-dom';
import { DropDownGroup } from '@hulu-react-style-components/dropdown';
import { IconCheck } from '@hulu-react-style-components/icons';
import React, { memo, useContext, useEffect, useState } from 'react';

import useUpdateLastActivity from '../../../apis/graphql/accountManagement/adAccount/useUpdateLastActivity';
import { AdAccountsByUserQuery } from '../../../apis/graphql/types';
import { BrandItem } from '../../../apis/graphql/user/useFetchAdAccountByUser/useFetchAdAccountByUser';
import { isBaymaxEnabledVar, selectedAdAccountIdVar } from '../../../constants';
import { GhostModeContext } from '../../../context/GhostMode/GhostMode';
import usePostLoginHeaderNavigate from '../hooks/usePostLoginHeaderNavigate';
import { StyledDropDown, StyledDropDownGlobalStyle, StyledDropDownItem } from './styled';
import { getRedirectUrl, getSelectedAdAccountData } from './utils';

type Props = {
  brandsWithAdAccounts: BrandItem[];
  disabled: boolean;
  disabledLabel: string;
  brandsData?: AdAccountsByUserQuery;
};

const AdAccountDropdown = ({
  brandsData,
  brandsWithAdAccounts,
  disabled = false,
  disabledLabel,
}: Props): JSX.Element | null => {
  const { pathname } = useLocation();
  const { isGhostMode } = useContext(GhostModeContext);
  const navigate = useNavigate();
  const selectedAdAccountId = useReactiveVar(selectedAdAccountIdVar);
  const isBaymaxEnabled = useReactiveVar(isBaymaxEnabledVar);
  const [selectedValue, setSelectedValue] = useState<string>(selectedAdAccountId || '');
  const { handleUpdateLastActivity } = useUpdateLastActivity();
  const { handleAdAccountDropdownClick } = usePostLoginHeaderNavigate(pathname);

  useEffect(() => {
    const selectedAdAccountIsExists = brandsWithAdAccounts.some((item) =>
      item.accountList.some(({ id }) => id === selectedAdAccountId)
    );

    if (selectedAdAccountId) {
      setSelectedValue(selectedAdAccountId);
      void handleUpdateLastActivity(selectedAdAccountId);
      return;
    }

    if (!!brandsWithAdAccounts?.length && !selectedAdAccountIsExists) {
      const adAccountId = brandsWithAdAccounts[0]?.accountList[0]?.id;

      selectedAdAccountIdVar(adAccountId);
      setSelectedValue(adAccountId);
      void handleUpdateLastActivity(adAccountId);

      if (!isGhostMode) {
        const accountData = getSelectedAdAccountData(adAccountId, brandsData?.adAccountsByUser.edges || []);
        navigate(getRedirectUrl(accountData, !!isBaymaxEnabled));
      }
    }
  }, [
    brandsData,
    brandsWithAdAccounts,
    handleUpdateLastActivity,
    isBaymaxEnabled,
    isGhostMode,
    navigate,
    selectedAdAccountId,
  ]);

  const handleChange = (id?: string): void => {
    if (id && selectedAdAccountId !== id) {
      selectedAdAccountIdVar(id);
      setSelectedValue(id);
      void handleUpdateLastActivity(id);

      if (!isGhostMode) {
        const accountData = getSelectedAdAccountData(id, brandsData?.adAccountsByUser.edges || []);
        const isBaymaxEnabled = accountData?.isBaymaxEnabled || false;

        navigate(getRedirectUrl(accountData, isBaymaxEnabled));
      } else {
        handleAdAccountDropdownClick(id);
      }
    }
  };

  if (!selectedAdAccountId) {
    return null;
  }

  return (
    <>
      <StyledDropDownGlobalStyle />
      <StyledDropDown
        value={selectedValue}
        disabled={disabled}
        isClearable={false}
        onChange={handleChange}
        data-testid="adAccountHeaderDropDown"
        isSearchable
      >
        {brandsWithAdAccounts.map((brandData) => (
          <DropDownGroup key={brandData.id} categoryName={brandData.name} data-testid="brandOptionItem">
            {brandData?.accountList.map((item) => (
              <StyledDropDownItem
                value={item.id}
                key={item.id}
                data-testid="accountOptionItem"
                className="ad-account-item"
                subtext={selectedValue === item.id && <IconCheck width={16} height={16} />}
              >
                {disabledLabel || item.label}
              </StyledDropDownItem>
            ))}
          </DropDownGroup>
        ))}
      </StyledDropDown>
    </>
  );
};

export default memo(AdAccountDropdown);
